import { useEffect, useMemo, useState, type DragEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EMPRESAS, EMPRESA_META, type Empresa, type LinkAssignment, type LinkSuggestion, type LinkSuggestions } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { Card, ErrorBox, Spinner, num } from "../components/ui.js";
import { AvailabilityEditor } from "../components/AvailabilityEditor.js";
import {
  DND_MIME, lerArrasto, montarPatch, ordenarCargas, ordenarPorScore, serializarArrasto, tomScore, type Carga,
} from "../lib/vincular.js";
import { fmtColetaDia, marcadores, paraInputHorario } from "../lib/carga.js";
import { OrdemColetaModal } from "../components/OrdemColetaModal.js";

const REFRESH_MS = 30_000;
type Arrasto = LinkAssignment & { id: string; score: number };
interface Resumo { competencia: string | null; competencias: string[] }

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const rotuloCompetencia = (c: string) => `${MESES[Number(c.slice(5, 7)) - 1]}/${c.slice(0, 4)}`;

const SCORE_CLS = {
  alto: "bg-emerald-100 text-emerald-900 ring-emerald-300",
  medio: "bg-amber-100 text-amber-900 ring-amber-300",
  nenhum: "bg-slate-100 text-slate-500 ring-slate-200",
} as const;

function Score({ valor, grande }: { valor: number; grande?: boolean }) {
  const tom = tomScore(valor);
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-lg font-bold ring-1 ${SCORE_CLS[tom]} ${grande ? "px-3 py-1.5 text-lg" : "px-2 py-0.5 text-xs"}`}
      title={valor > 0 ? "Evidência de vínculo com a carga (0–100)" : "Nenhuma fonte associa este motorista à carga"}
      data-testid="score"
      data-score={valor}
    >
      {valor > 0 ? <>{valor}<span className="ml-0.5 text-[0.65em] font-normal opacity-70">/100</span></> : "sem vínculo"}
    </span>
  );
}

const FIT_CLS: Record<string, string> = {
  ready: "bg-emerald-50 text-emerald-800",
  expected: "bg-sky-50 text-sky-800",
  confirm: "bg-slate-100 text-slate-600",
  conflict: "bg-red-50 text-red-800",
};

/* ------------------------------------------------------------------------ */
/*  Cartão de motorista (arrastável)                                         */
/* ------------------------------------------------------------------------ */
function CartaoMotorista({ s, posicao, onVincular, onArrastar, arrastando, podeEditar }: {
  s: LinkSuggestion; posicao: number; onVincular: () => void; podeEditar: boolean;
  onArrastar: (a: Arrasto | null) => void; arrastando: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const [editando, setEditando] = useState(false);
  const av = s.availability;
  return (
    <li
      draggable
      data-testid="motorista-card"
      data-motorista={s.motorista}
      data-score={s.score}
      onDragStart={(e) => {
        e.dataTransfer.setData(DND_MIME, serializarArrasto(s));
        e.dataTransfer.setData("text/plain", s.motorista);
        e.dataTransfer.effectAllowed = "link";
        onArrastar(s);
      }}
      onDragEnd={() => onArrastar(null)}
      className={`group cursor-grab rounded-xl border bg-white p-3 shadow-sm transition-shadow active:cursor-grabbing hover:shadow-md ${arrastando ? "opacity-40 ring-2 ring-brand-400" : "border-slate-200"}`}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 w-5 shrink-0 text-center text-xs font-semibold text-slate-400" aria-hidden>{posicao}</span>
        <span className="mt-1 shrink-0 select-none text-slate-300 group-hover:text-brand-500" aria-hidden title="Arraste para a carga">⠿</span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-slate-900" title={s.motorista}>{s.motorista}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            Cavalo <b className="text-slate-700">{s.cavalo || "—"}</b>
            {s.primeira_carreta && <> · {s.primeira_carreta}</>}
            {s.segunda_carreta && <> · {s.segunda_carreta}</>}
          </p>
          {av && (
            <span className={`mt-1.5 inline-block rounded-md px-1.5 py-0.5 text-[11px] font-medium ${FIT_CLS[av.fit] ?? FIT_CLS.confirm}`} data-fit={av.fit}>
              {av.label}
              {av.route && <> · {av.route.distanceKm.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} km</>}
            </span>
          )}
        </div>
        <Score valor={s.score} />
      </div>
      <div className="mt-2 flex items-center gap-2 pl-8">
        <button type="button" className="rounded-lg bg-brand-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-700" onClick={onVincular} data-testid="motorista-vincular">
          Vincular à carga
        </button>
        <button type="button" className="text-xs text-brand-700 hover:underline" onClick={() => setAberto(!aberto)} aria-expanded={aberto}>
          {aberto ? "menos" : "por quê?"}
        </button>
        {podeEditar && (
          <button type="button" className="ml-auto text-xs text-slate-500 hover:underline" onClick={() => setEditando(!editando)}>
            disponibilidade
          </button>
        )}
      </div>
      {aberto && (
        <div className="mt-2 rounded-lg bg-slate-50 p-2 text-xs leading-relaxed text-slate-600">
          {s.sources.length > 0 && <p className="mb-1 text-slate-500">Fontes: {s.sources.join(" · ")}</p>}
          <ul className="space-y-0.5">{s.reasons.map((r, i) => <li key={i}>{s.score > 0 && <b>{r.points > 0 ? "+" : ""}{r.points}</b>} {r.text}</li>)}</ul>
          {s.warnings.length > 0 && <ul className="mt-1 space-y-0.5 text-amber-800">{s.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
          {av && av.reasons.length > 0 && <ul className="mt-1 space-y-0.5 text-slate-500">{av.reasons.map((r) => <li key={r}>{r}</li>)}</ul>}
        </div>
      )}
      {editando && <div className="mt-2"><AvailabilityEditor driver={s.motorista} onClose={() => setEditando(false)} /></div>}
    </li>
  );
}

/* ------------------------------------------------------------------------ */
/*  Cartão de carga (alvo do arrasto)                                        */
/* ------------------------------------------------------------------------ */
function CartaoCarga({ c, ativa, onSelecionar, arrastando, pendente, onSoltar, onConfirmar, onCancelar, salvando, erro, onEditar }: {
  c: Carga; ativa: boolean; onSelecionar: () => void; arrastando: boolean;
  pendente: Arrasto | null; onSoltar: (a: Arrasto) => void; onConfirmar: (horario: string | null) => void; onCancelar: () => void;
  salvando: boolean; erro: unknown; onEditar?: () => void;
}) {
  const [sobre, setSobre] = useState(false);
  // Data e hora que o gestor SOLICITA junto com o vínculo. Começa no pedido
  // que já existir (ou na coleta já confirmada) para não apagar nada sem querer.
  const [horario, setHorario] = useState(() => paraInputHorario(c.data_hora_solicitada ?? c.data_hora_coleta));
  useEffect(() => { if (pendente) setHorario(paraInputHorario(c.data_hora_solicitada ?? c.data_hora_coleta)); }, [pendente, c.data_hora_solicitada, c.data_hora_coleta]);
  const aceita = (e: DragEvent) => Array.from(e.dataTransfer.types).includes(DND_MIME);
  const score = c.score_vinculo ?? 0;
  const origem = c.unidade_origem || c.patio_coleta;
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="carga-card"
      data-ordem={c.numero_ordem}
      data-ativa={ativa}
      onClick={onSelecionar}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelecionar(); } }}
      onDragOver={(e) => { if (aceita(e)) { e.preventDefault(); e.dataTransfer.dropEffect = "link"; setSobre(true); } }}
      onDragLeave={() => setSobre(false)}
      onDrop={(e) => {
        e.preventDefault(); setSobre(false);
        const a = lerArrasto(e.dataTransfer.getData(DND_MIME));
        if (a) onSoltar(a);
      }}
      className={`relative cursor-pointer rounded-xl border-2 bg-white p-4 text-left transition-colors ${
        sobre ? "border-brand-500 bg-brand-50 ring-4 ring-brand-200"
        : ativa ? "border-brand-500 shadow-md"
        : arrastando ? "border-dashed border-brand-300"
        : "border-slate-200 hover:border-brand-300"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{EMPRESA_META[c.empresa as Empresa]?.label ?? c.empresa} · ordem</p>
          <p className="truncate text-lg font-bold text-slate-900">{c.numero_ordem}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Score valor={score} />
          {onEditar && (
            <button type="button" className="text-[11px] font-medium text-brand-700 hover:underline" onClick={(e) => { e.stopPropagation(); onEditar(); }} data-testid="carga-editar">
              editar carga
            </button>
          )}
        </div>
      </div>
      {/* A cidade fica numa linha PRÓPRIA e em destaque. Na mesma linha do
          cliente ela era cortada pelo "truncate" — e muitas entregas são para
          o mesmo cliente em cidades diferentes, então sem a cidade os cartões
          ficavam indistinguíveis (feedback da torre, out/2026). */}
      <p className="mt-2 flex items-center gap-1.5 text-base font-semibold text-slate-900" data-testid="carga-cidade">
        <span aria-hidden className="text-brand-600">📍</span>
        {c.cidade_entrega
          ? <span className="truncate" title={c.cidade_entrega}>{c.cidade_entrega}</span>
          : <span className="text-sm font-medium text-amber-700">cidade não informada</span>}
      </p>
      <p className="mt-0.5 truncate text-sm text-slate-600" title={c.cliente_entrega ?? ""}>
        {c.cliente_entrega || "cliente não informado"}
      </p>
      {marcadores(c).length > 0 && (
        <p className="mt-1.5 flex flex-wrap gap-1" data-testid="selos-carga">
          {marcadores(c).map((m) => <span key={m.chave} className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${m.cls}`}>{m.label}</span>)}
        </p>
      )}
      <p className="mt-1.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
        <span>coleta <b className="text-slate-700">{c.data_hora_coleta ? fmtColetaDia(c.data_hora_coleta) : "sem data"}</b></span>
        {c.data_hora_solicitada && !c.data_hora_coleta && <span>solicitado <b className="text-amber-700">{fmtColetaDia(c.data_hora_solicitada)}</b></span>}
        {origem && <span>de <b className="text-slate-700">{origem}</b></span>}
        {c.tipo_veiculo && <span>veículo <b className="text-slate-700">{c.tipo_veiculo}</b></span>}
        {c.peso != null && <span>{num(c.peso)} t</span>}
      </p>

      {pendente ? (
        <div className="mt-3 rounded-lg border border-brand-300 bg-brand-50 p-3" data-testid="confirmar-vinculo" onClick={(e) => e.stopPropagation()}>
          <p className="text-sm text-slate-800">
            Vincular <b>{pendente.motorista}</b>{pendente.cavalo && <> (cavalo {pendente.cavalo})</>} a esta carga?
          </p>
          <p className="mt-0.5 text-xs text-slate-500">A carga sai de <b>disponíveis</b> e vai para <b>agendadas</b>.</p>
          <label className="mt-2 block text-xs font-semibold text-slate-700">
            Solicitar data e hora da coleta
            <input
              type="datetime-local"
              className="input mt-1 w-full text-sm"
              value={horario}
              onChange={(e) => setHorario(e.target.value)}
              data-testid="vinculo-horario"
            />
          </label>
          <p className="mt-1 text-[11px] leading-snug text-slate-500">
            O pedido vai para a <b>Torre de Controle</b>: o analista confere, agenda com o cliente e confirma este horário ou ajusta.
          </p>
          {erro != null && <div className="mt-2"><ErrorBox error={erro} /></div>}
          <div className="mt-2 flex gap-2">
            <button type="button" className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50" disabled={salvando} onClick={() => onConfirmar(horario || null)} data-testid="confirmar-vinculo-ok">
              {salvando ? "Salvando…" : "Confirmar vínculo"}
            </button>
            <button type="button" className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100" disabled={salvando} onClick={onCancelar}>
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <p className={`mt-3 rounded-lg border border-dashed px-3 py-2 text-center text-xs ${sobre ? "border-brand-500 text-brand-800" : ativa ? "border-brand-300 text-brand-700" : "border-slate-200 text-slate-400"}`}>
          {sobre ? "Solte para vincular" : ativa ? "Arraste um motorista do painel para cá" : "Clique para ver motoristas · ou solte um aqui"}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/*  Página                                                                   */
/* ------------------------------------------------------------------------ */
export function Vincular() {
  const { canWrite, can } = useAuth();
  const qc = useQueryClient();
  // Editar/criar a carga é da torre; VINCULAR (motorista + horário solicitado)
  // também é do gestor — é ele quem aloca o motorista nesta tela (out/2026).
  const podeEditarCarga = canWrite("viagem_vega");
  const writable = podeEditarCarga || can("vinculo");
  const [ordem, setOrdem] = useState<Record<string, unknown> | null>(null);
  const [competencia, setCompetencia] = useState<string | null>(null);
  const [empresa, setEmpresa] = useState<Empresa | "">("");
  const [busca, setBusca] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [ativaId, setAtivaId] = useState<number | null>(null);
  const [arrastando, setArrastando] = useState<Arrasto | null>(null);
  const [pendente, setPendente] = useState<{ cargaId: number; a: Arrasto } | null>(null);
  type Envio = { cargaId: number; a: Arrasto; horario: string | null };
  const [filtroMotorista, setFiltroMotorista] = useState("");
  const [margem, setMargem] = useState(2);
  const [feitos, setFeitos] = useState<{ id: number; ordem: string; motorista: string }[]>([]);

  useEffect(() => { const t = setTimeout(() => setBuscaDebounced(busca.trim()), 300); return () => clearTimeout(t); }, [busca]);

  const resumo = useQuery({
    queryKey: ["vincular-resumo"],
    queryFn: async () => (await api.get("/operacoes/torre?priorizar_vinculos=true")).data as Resumo,
  });
  const competenciaAtiva = competencia ?? resumo.data?.competencia ?? null;
  const ano = competenciaAtiva?.slice(0, 4);
  const mes = competenciaAtiva ? String(Number(competenciaAtiva.slice(5, 7))) : undefined;

  const cargas = useQuery({
    queryKey: ["vincular-cargas", ano, mes, empresa, buscaDebounced],
    enabled: !!competenciaAtiva,
    refetchInterval: REFRESH_MS,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const qs = new URLSearchParams({ fase: "DISPONIVEL", priorizar_vinculos: "true", limit: "300" });
      if (empresa) qs.set("empresa", empresa);
      if (buscaDebounced) qs.set("q", buscaDebounced);
      else if (ano && mes) { qs.set("ano", ano); qs.set("mes", mes); }
      return ordenarCargas((await api.get(`/operacoes?${qs}`)).data as Carga[]);
    },
  });

  const lista = cargas.data ?? [];
  // A carga ativa é a clicada; sem clique, a de maior score — o painel da
  // direita nunca fica vazio esperando uma seleção.
  const ativa = lista.find((c) => c.id === ativaId) ?? lista[0] ?? null;

  const sugestoes = useQuery({
    queryKey: ["vincular-sugestoes", ativa?.id, ativa?.numero_ordem, filtroMotorista, margem],
    enabled: !!ativa,
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const body = {
        numero_ordem: ativa!.numero_ordem,
        cidade_entrega: ativa!.cidade_entrega ?? "",
        motorista: ativa!.motorista ?? "",
        cavalo: ativa!.cavalo ?? "",
        data_hora_coleta: ativa!.data_hora_coleta ? `${ativa!.data_hora_coleta.slice(0, 19)}-03:00` : "",
        cidade_coleta: ativa!.unidade_origem ?? ativa!.patio_coleta ?? "",
        margem_horas: margem,
        busca: filtroMotorista,
      };
      const r = (await api.post("/operacoes/sugestoes-vinculo", body)).data as LinkSuggestions;
      return { ...r, rows: ordenarPorScore(r.rows) };
    },
  });

  const salvar = useMutation({
    mutationFn: async ({ cargaId, a, horario }: Envio) =>
      (await api.post(`/operacoes/${cargaId}/vincular`, { ...montarPatch(a), data_hora_solicitada: horario })).data as { id: number },
    onSuccess: (_d, { cargaId, a }) => {
      const c = lista.find((x) => x.id === cargaId);
      setFeitos((f) => [{ id: cargaId, ordem: c?.numero_ordem ?? String(cargaId), motorista: a.motorista }, ...f].slice(0, 8));
      setPendente(null);
      if (ativaId === cargaId) setAtivaId(null);
      qc.invalidateQueries({ queryKey: ["vincular-cargas"] });
      qc.invalidateQueries({ queryKey: ["vincular-sugestoes"] });
      qc.invalidateQueries({ queryKey: ["torre-grid"] });
      qc.invalidateQueries({ queryKey: ["torre-resumo"] });
    },
  });

  const propor = (cargaId: number, a: Arrasto) => { salvar.reset(); setAtivaId(cargaId); setPendente({ cargaId, a }); };

  const comVinculo = lista.filter((c) => (c.score_vinculo ?? 0) > 0).length;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vincular carga</h1>
          <p className="mt-1 text-sm text-slate-500">
            Escolha uma carga à esquerda e arraste o motorista do painel à direita para ela. Os motoristas aparecem ordenados por evidência de vínculo com a carga selecionada.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select className="input w-36" data-testid="vincular-competencia" value={competenciaAtiva ?? ""} onChange={(e) => { setCompetencia(e.target.value); setAtivaId(null); }}>
            {(resumo.data?.competencias ?? []).map((c) => <option key={c} value={c}>{rotuloCompetencia(c)}</option>)}
          </select>
          <select className="input w-40" data-testid="vincular-empresa" value={empresa} onChange={(e) => { setEmpresa(e.target.value as Empresa | ""); setAtivaId(null); }}>
            <option value="">Todas as empresas</option>
            {EMPRESAS.map((e) => <option key={e} value={e}>{EMPRESA_META[e].label}</option>)}
          </select>
          <input className="input w-56" data-testid="vincular-busca" placeholder="Buscar ordem, cliente, cidade…" value={busca} onChange={(e) => setBusca(e.target.value)} />
          {podeEditarCarga && (
            <button type="button" className="btn-primary" data-testid="nova-ordem" onClick={() => setOrdem({ empresa: empresa || undefined, competencia: competenciaAtiva ?? undefined })}>
              + Nova ordem de coleta
            </button>
          )}
        </div>
      </div>

      {!writable && (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Seu perfil permite apenas consultar. A vinculação está disponível para <b>Gestor</b>, <b>Torre</b> e <b>Administrador</b>.
        </div>
      )}

      {feitos.length > 0 && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-900" data-testid="vinculos-feitos">
          <b>Vinculadas agora:</b>{" "}
          {feitos.map((f, i) => <span key={f.id}>{i > 0 && " · "}ordem {f.ordem} → {f.motorista}</span>)}
        </div>
      )}

      {ordem && (
        <OrdemColetaModal
          inicial={ordem}
          onClose={() => setOrdem(null)}
          onSalvo={() => {
            setOrdem(null);
            qc.invalidateQueries({ queryKey: ["vincular-cargas"] });
            qc.invalidateQueries({ queryKey: ["torre-grid"] });
            qc.invalidateQueries({ queryKey: ["torre-resumo"] });
          }}
        />
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        {/* ------------------------------ cargas ------------------------------ */}
        <section aria-label="Cargas disponíveis">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="font-semibold text-slate-800">
              Cargas disponíveis <span className="text-sm font-normal text-slate-500">· {num(lista.length)}{comVinculo > 0 && <>, {num(comVinculo)} com vínculo comprovado</>}</span>
            </h2>
            {cargas.isFetching && <span className="text-xs text-brand-600">atualizando…</span>}
          </div>
          {cargas.isError ? <ErrorBox error={cargas.error} />
            : cargas.isLoading || resumo.isLoading ? <Spinner />
            : lista.length === 0 ? <Card><p className="text-sm text-slate-500">Nenhuma carga disponível neste filtro. Troque a competência ou a empresa.</p></Card>
            : (
              <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3" data-testid="cargas-grid">
                {lista.map((c) => (
                  <CartaoCarga
                    key={c.id}
                    c={c}
                    ativa={ativa?.id === c.id}
                    onSelecionar={() => { setAtivaId(c.id); if (pendente && pendente.cargaId !== c.id) setPendente(null); }}
                    arrastando={!!arrastando}
                    pendente={pendente?.cargaId === c.id ? pendente.a : null}
                    onSoltar={(a) => writable && propor(c.id, a)}
                    onConfirmar={(horario) => pendente && salvar.mutate({ ...pendente, horario })}
                    onEditar={podeEditarCarga ? () => setOrdem(c as unknown as Record<string, unknown>) : undefined}
                    onCancelar={() => { setPendente(null); salvar.reset(); }}
                    salvando={salvar.isPending && salvar.variables?.cargaId === c.id}
                    erro={salvar.isError && salvar.variables?.cargaId === c.id ? salvar.error : null}
                  />
                ))}
              </div>
            )}
        </section>

        {/* ---------------------------- motoristas ---------------------------- */}
        <aside className="lg:sticky lg:top-4 lg:self-start" aria-label="Motoristas por score" data-testid="painel-motoristas">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="font-semibold text-slate-800">Motoristas</h2>
              {sugestoes.isFetching && <span className="text-xs text-brand-600">atualizando…</span>}
            </div>
            {ativa ? (
              <p className="mt-0.5 text-xs text-slate-500">
                para a ordem <b className="text-slate-700" data-testid="painel-ordem">{ativa.numero_ordem}</b>
                {ativa.cidade_entrega && <> → {ativa.cidade_entrega}</>} · ordenados por score
              </p>
            ) : <p className="mt-0.5 text-xs text-slate-500">Selecione uma carga para ver os motoristas.</p>}

            <div className="mt-3 flex gap-2">
              <input className="input flex-1 text-sm" placeholder="Filtrar motorista ou cavalo" value={filtroMotorista} onChange={(e) => setFiltroMotorista(e.target.value)} data-testid="filtro-motorista" />
              <label className="flex items-center gap-1 text-xs text-slate-500" title="Margem após a liberação (horas)">
                +<input type="number" min={0} max={168} className="input w-14 text-sm" value={margem} onChange={(e) => setMargem(Math.max(0, Math.min(168, Number(e.target.value))))} />h
              </label>
            </div>

            {!ativa ? null
              : sugestoes.isError ? <div className="mt-3"><ErrorBox error={sugestoes.error} /></div>
              : sugestoes.isPending ? <p role="status" className="mt-3 text-sm text-slate-600">Comparando transporte, motorista e placas…</p>
              : sugestoes.data.rows.length === 0 ? <p className="mt-3 text-sm text-slate-600">Nenhum motorista para este filtro.</p>
              : (
                <ol className="mt-3 max-h-[70vh] space-y-2 overflow-y-auto pr-1" data-testid="motoristas-lista">
                  {sugestoes.data.rows.map((s, i) => (
                    <CartaoMotorista
                      key={s.id}
                      s={s}
                      posicao={i + 1}
                      podeEditar={writable}
                      arrastando={arrastando?.id === s.id}
                      onArrastar={setArrastando}
                      onVincular={() => writable && ativa && propor(ativa.id, s)}
                    />
                  ))}
                </ol>
              )}
            {ativa && sugestoes.data && (
              <p className="mt-2 text-[11px] text-slate-400">
                {sugestoes.data.rows.length} de {sugestoes.data.total ?? sugestoes.data.rows.length} · o score mede evidência de vínculo, não disponibilidade.
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
